import axios from "./dvdlocator";

export interface ColumnSet {
  listingColumns: string[];
  inventoryColumns: string[];
}

export interface ColumnSetsResponse {
  [columnSetName: string]: ColumnSet;
}

export interface ColumnSetEntry {
  columnSetName: string;
  columns: ColumnSet;
}

export class ColumnSetsManager {
  /**
   * Fetches column sets for a specific user by their userId
   * @param userId - The username or local-part of the email address of the signed-in Amplify user
   * @returns Array of column set entries with name and columns
   */
  static async getColumnSetsForUserId(
    userId: string
  ): Promise<ColumnSetEntry[]> {
    try {
      const response = await axios.get<{ column_sets: ColumnSetsResponse }>(
        "/nosql/column-sets",
        {
          params: { username: userId },
        }
      );

      const columnSets = Object.entries(response.data.column_sets).map(
        ([columnSetName, columns]) => ({
          columnSetName,
          columns,
        })
      );

      return columnSets;
    } catch (err) {
      console.error("Error fetching column sets:", err);
      throw err;
    }
  }

  /**
   * Transforms a column set into a columnVisibilityModel for MUI DataGrid
   * @param allColumns - Array of all column definitions in the DataGrid
   * @param selectedColumns - Array of column field names that should be visible
   * @returns Record mapping column field names to visibility boolean
   */
  static transformToColumnVisibilityModel(
    allColumns: { field: string; hideable?: boolean }[],
    selectedColumns: string[]
  ): Record<string, boolean> {
    const columnVisibilityModel: Record<string, boolean> = {};

    // Filter out columns that are NOT in the selectedColumns array
    // and set them to false (hidden), unless they have hideable: false
    allColumns.forEach((column) => {
      if (!selectedColumns.includes(column.field)) {
        // Only hide columns that are allowed to be hidden (hideable !== false)
        if (column.hideable !== false) {
          columnVisibilityModel[column.field] = false;
        }
      }
    });

    return columnVisibilityModel;
  }

  /**
   * Reorders columns based on a column set's order
   * @param allColumns - Array of all column definitions
   * @param columnSetOrder - Array of column field names in the desired order from the column set
   * @returns Reordered array of column definitions
   */
  static reorderColumns<T extends { field: string; hideable?: boolean }>(
    allColumns: T[],
    columnSetOrder: string[]
  ): T[] {
    // Create a map for quick lookup of column definitions by field name
    const columnMap = new Map<string, T>();
    allColumns.forEach((col) => {
      columnMap.set(col.field, col);
    });

    // Build the reordered array based on columnSetOrder
    const reorderedColumns: T[] = [];
    const usedFields = new Set<string>();

    // First, add non-hideable columns at the start (in their original order)
    allColumns.forEach((col) => {
      if (col.hideable === false) {
        reorderedColumns.push(col);
        usedFields.add(col.field);
      }
    });

    // Then, add columns in the order specified by the column set
    columnSetOrder.forEach((field) => {
      if (!usedFields.has(field)) {
        const column = columnMap.get(field);
        if (column) {
          reorderedColumns.push(column);
          usedFields.add(field);
        }
      }
    });

    // Finally, append any remaining columns not in the column set (in their original order)
    allColumns.forEach((col) => {
      if (!usedFields.has(col.field)) {
        reorderedColumns.push(col);
      }
    });

    return reorderedColumns;
  }
}
